import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, Tooltip, Legend,
} from 'chart.js';
import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchFootfallDemographic } from '../../api';
import { ChartSkeleton } from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import styles from './Charts.module.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtFull(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]}, ${y}`;
}

export default function DemographicAnalysis() {
  const { currentStore, visibleStores, startDate, endDate } = useDashboard();

  const storeIds = currentStore
    ? currentStore.storeId
    : visibleStores.map(s => s.storeId).join(',');

  const cacheKey = storeIds && startDate && endDate
    ? `demographic:${storeIds}:${startDate}:${endDate}`
    : null;
  const { data, loading } = useCache(cacheKey, () => fetchFootfallDemographic(storeIds, startDate, endDate));

  const labels = data?.ageBands || [];
  const male = data?.male || [];
  const female = data?.female || [];

  const sub = startDate && endDate
    ? `Based on ${fmtFull(startDate)} – ${fmtFull(endDate)}`
    : '';

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Male',
        data: male,
        backgroundColor: '#7ec8e3',
        borderRadius: 0,
        maxBarThickness: 50,
      },
      {
        label: 'Female',
        data: female,
        backgroundColor: '#5b7fd4',
        borderRadius: 0,
        maxBarThickness: 50,
      },
    ],
  };

  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: { boxWidth: 12, boxHeight: 12, padding: 14, color: '#667085', font: { size: 12 } },
      },
      tooltip: {
        callbacks: { label: item => ` ${item.dataset.label}: ${item.raw.toLocaleString()}` },
        backgroundColor: '#1f2937', titleColor: '#f9fafb',
        bodyColor: '#d1d5db', padding: 8, cornerRadius: 6,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#667085', font: { size: 11 } } },
      y: {
        grid: { color: 'rgba(0,0,0,0.04)', drawTicks: false },
        border: { display: false },
        ticks: { color: '#667085', font: { size: 11 }, callback: v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v },
      },
    },
  };

  const hasData = labels.length > 0 && (male.some(v => v > 0) || female.some(v => v > 0));

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>Demographic Analysis</div>
          <div className={styles.sub}>{sub}</div>
        </div>
      </div>
      {loading ? (
        <ChartSkeleton height={200} />
      ) : !hasData ? (
        <EmptyState message="No demographic data available" />
      ) : (
        <div className={styles.chartWrap}>
          <Bar data={chartData} options={options} />
        </div>
      )}
    </div>
  );
}
